import {
	BookOpen,
	Check,
	Copy,
	FileText,
	LayoutDashboard,
	ListChecks,
	ListTodo,
	Minus,
	Plug,
	Server,
	Smartphone,
	TrendingUp,
	Users,
	Wrench,
} from "lucide-react";
import { NightDecor } from "@/components/blocks/decor/Decor";
import { BlueprintScene } from "@/components/blocks/scenes/BlueprintScene";
import { BudgetTimeline } from "@/components/blocks/scenes/BudgetTimeline";
import { BusinessAppCost } from "@/components/blocks/scenes/CostCompare";
import { CostCurves } from "@/components/blocks/scenes/CostCurves";
import { FieldReportScene } from "@/components/blocks/scenes/FieldReportScene";
import { CardGrid, toCardItems } from "@/components/blocks/sections/CardGrid";
import { Cta } from "@/components/blocks/sections/Cta";
import { Faq } from "@/components/blocks/sections/Faq";
import { ListCard } from "@/components/blocks/sections/ListCard";
import { NumberedPoints } from "@/components/blocks/sections/NumberedPoints";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { ProofCard } from "@/components/blocks/sections/ProofCard";
import { Section } from "@/components/blocks/sections/Section";
import { SectionHeading } from "@/components/blocks/sections/SectionHeading";
import { StickyStory } from "@/components/blocks/sections/StickyStory";
import { Reveal } from "@/components/motion/motion";
import { useSectionViewTracking } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";
import { ServiceHeroActions } from "./ServiceHeroActions";

const SECTION_IDS = {
	situations: "situations",
	economics: "cout",
	saas: "abonnement",
	solutions: "solutions",
	usages: "usages",
	process: "deroulement",
	deliverables: "livrables",
	after: "apres-livraison",
	budget: "budget",
	proof: "preuve",
	faq: "questions",
	cta: "contact",
} as const;

const SECTIONS = Object.values(SECTION_IDS).map((id) => ({ id, label: id }));

const SITUATION_ICONS = {
	reentry: Copy,
	reports: FileText,
	tracking: ListChecks,
	clients: Users,
} as const;

const SOLUTION_ICONS = {
	collection: Smartphone,
	documents: FileText,
	tracking: ListTodo,
	portal: LayoutDashboard,
	integrations: Plug,
} as const;

const AFTER_ICONS = {
	hosting: Server,
	maintenance: Wrench,
	evolutions: TrendingUp,
	handover: BookOpen,
} as const;

/** Blueprint stage shown for each of the six process steps. */
const PROCESS_STAGES = [0, 0, 1, 2, 3, 3] as const;

export function BusinessAppsPage() {
	const { dict, locale } = useI18n();
	useSectionViewTracking(SECTIONS, { pageType: "service", locale });
	const page = dict.businessApps;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				visual={<FieldReportScene className="mx-auto max-w-[36rem]" />}
			>
				<ServiceHeroActions
					need="businessApps"
					placement="business_apps_hero"
					primary={page.hero.primaryCta}
					secondary={page.hero.secondaryCta}
					processId={SECTION_IDS.process}
				/>
			</PageHero>

			<Section id={SECTION_IDS.situations}>
				<SectionHeading
					index="01"
					label={page.situations.label}
					title={page.situations.title}
					description={page.situations.description}
				/>
				<CardGrid
					className="mt-14"
					columns={4}
					items={toCardItems(page.situations.items, {
						icons: SITUATION_ICONS,
					})}
				/>
			</Section>

			<Section id={SECTION_IDS.economics} tone="stone">
				<SectionHeading
					index="02"
					label={page.economics.label}
					title={page.economics.title}
					description={page.economics.description}
				/>
				<div className="mt-14 grid items-start gap-10 lg:grid-cols-2 lg:gap-16">
					<Reveal>
						<BusinessAppCost />
					</Reveal>
					<NumberedPoints
						title={dict.economics.methodTitle}
						items={dict.economics.method}
					/>
				</div>
			</Section>

			<Section id={SECTION_IDS.saas}>
				<SectionHeading
					index="03"
					label={page.saas.label}
					title={page.saas.title}
					description={page.saas.description}
				/>
				<div className="mt-14 grid items-start gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
					<Reveal>
						<CostCurves />
					</Reveal>
					<div className="grid gap-5">
						{(["subscription", "custom"] as const).map((key) => {
							const Icon = key === "custom" ? Check : Minus;
							return (
								<Reveal
									key={key}
									delay={key === "custom" ? 0.15 : 0}
									className={cn(
										"rounded-3xl border p-6 md:p-8",
										key === "custom"
											? "border-signal/50 bg-card"
											: "bg-muted/50",
									)}
								>
									<h3 className="text-lg font-semibold tracking-tight">
										{page.saas[key].title}
									</h3>
									<ul className="mt-4 space-y-3">
										{Object.entries(page.saas[key].items).map(
											([item, text]) => (
												<li key={item} className="flex gap-3 text-pretty">
													<span
														aria-hidden="true"
														className={cn(
															"mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border",
															key === "custom"
																? "border-signal/40 bg-signal/10 text-signal-ink"
																: "text-muted-foreground",
														)}
													>
														<Icon className="size-3" strokeWidth={2.5} />
													</span>
													<span
														className={cn(
															key === "subscription" && "text-muted-foreground",
														)}
													>
														{text}
													</span>
												</li>
											),
										)}
									</ul>
								</Reveal>
							);
						})}
					</div>
				</div>
				<p className="mt-6 max-w-2xl text-sm text-pretty text-muted-foreground">
					{page.saas.note}
				</p>
			</Section>

			<Section id={SECTION_IDS.solutions} tone="stone">
				<SectionHeading
					index="04"
					label={page.solutions.label}
					title={page.solutions.title}
					description={page.solutions.description}
				/>
				<CardGrid
					className="mt-14"
					items={toCardItems(page.solutions.items, {
						icons: SOLUTION_ICONS,
						badge: dict.common.exampleBadge,
					})}
				/>
			</Section>

			<Section id={SECTION_IDS.usages}>
				<SectionHeading
					index="05"
					label={page.usages.label}
					title={page.usages.title}
					description={page.usages.description}
				/>
				<CardGrid
					className="mt-14"
					columns={2}
					items={Object.entries(page.usages.items).map(([key, usage]) => ({
						key,
						title: usage.title,
						details: [
							{ label: page.usages.benefitLabel, text: usage.benefit },
							{ label: page.usages.checkLabel, text: usage.check },
						],
					}))}
				/>
			</Section>

			<Section id={SECTION_IDS.process} tone="stone">
				<SectionHeading
					index="06"
					label={page.process.label}
					title={page.process.title}
					description={page.process.description}
				/>
				<StickyStory
					className="mt-16 md:mt-24"
					steps={Object.entries(page.process.steps).map(([key, step]) => ({
						key,
						title: step.title,
						description: step.description,
					}))}
					renderVisual={(index) => (
						<BlueprintScene stage={PROCESS_STAGES[index] ?? 0} />
					)}
				/>
			</Section>

			<Section id={SECTION_IDS.deliverables}>
				<div className="grid gap-5 lg:grid-cols-2">
					<ListCard
						label={page.deliverables.label}
						title={page.deliverables.title}
						items={page.deliverables.items}
					/>
					<ListCard
						label={page.fit.label}
						title={page.fit.title}
						description={page.fit.description}
						items={page.fit.items}
						className="bg-muted/60"
					/>
				</div>
			</Section>

			<Section id={SECTION_IDS.after} tone="stone">
				<SectionHeading
					index="07"
					label={page.after.label}
					title={page.after.title}
					description={page.after.description}
				/>
				<CardGrid
					className="mt-14"
					columns={4}
					items={toCardItems(page.after.items, { icons: AFTER_ICONS })}
				/>
			</Section>

			<Section id={SECTION_IDS.budget}>
				<SectionHeading
					index="08"
					label={page.budget.label}
					title={page.budget.title}
					description={page.budget.description}
				/>
				<Reveal className="mt-14">
					<BudgetTimeline
						items={Object.entries(page.budget.items).map(([key, item]) => ({
							key,
							...item,
						}))}
					/>
				</Reveal>
				<p className="mt-6 max-w-2xl text-sm text-pretty text-muted-foreground">
					{page.budget.note}
				</p>
				<Reveal
					as="figure"
					className="mt-20 grid gap-6 border-t pt-12 md:grid-cols-[auto_1fr] md:gap-10"
				>
					<span
						aria-hidden="true"
						className="font-mono text-7xl leading-none font-bold text-signal-ink"
					>
						“
					</span>
					<blockquote className="max-w-4xl text-[clamp(1.5rem,2.8vw,2.25rem)] leading-tight font-medium tracking-[-0.02em] text-balance">
						{page.reassurance}
					</blockquote>
				</Reveal>
			</Section>

			<Section id={SECTION_IDS.proof} tone="night" decor={<NightDecor />}>
				<ProofCard {...page.proof} />
			</Section>

			<Section id={SECTION_IDS.faq} tone="stone">
				<Faq title={page.faq.title} items={page.faq.items} />
			</Section>

			<Cta
				id={SECTION_IDS.cta}
				{...page.cta}
				need="businessApps"
				placement="business_apps_cta"
			/>
		</>
	);
}
